import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as routes from '../utils/routes';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { CircularProgress, Typography, Avatar, Menu, MenuItem, Button, Paper } from '@mui/material';
import ReactGA from 'react-ga4';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MailIcon from '@mui/icons-material/Mail';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import SidePanel from '../components/SidePanel';
import { useSelector } from 'react-redux';
import { CurrencyCode } from '../types/currencies';
import currencies from '../utils/currencies.json';

// Helper function to get website URL
const getWebsiteUrl = () => {
	// Check if we're in development mode
	const isDevelopment = process.env.NODE_ENV === 'development';
	const websiteUrl =
		process.env.REACT_APP_WEBSITE_URL ||
		(isDevelopment ? 'http://localvendready.com:3000' : 'https://vendready.store');
	// Remove protocol for subdomain links
	return websiteUrl.replace(/^https?:\/\//, '').replace(/^http?:\/\//, '');
};

const generateSubdomainUrl = (subdomain: string) => {
	if (!subdomain) return '';
	// Ensure subdomain is lowercase and trimmed
	const formattedSubdomain = subdomain.toLowerCase().trim();
	const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https'; // Use http for development, https for production
	return `${protocol}://${formattedSubdomain}.${getWebsiteUrl()}`;
};
// Styled components
const Wrapper = styled.div`
	min-height: 100vh;
	background-color: #f9fafb;
	padding: 50px;
	padding-top: 100px;
	@media (max-width: 768px) {
		padding: 20px;
		padding-top: 90px;
	}
`;

const Header = styled.header`
	position: fixed;
	top: 0px;
	left: 0px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: white;
	margin-bottom: 40px;
	width: 100vw;
	border-bottom: 1px solid #eaeaea;
	z-index: 3; /* Set higher than Bar */
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	padding: 20px 40px;

	@media (max-width: 768px) {
		padding: 10px 20px;
	}
`;

const Title = styled.p`
	margin: 0px;
	font-size: 2rem;
	letter-spacing: 0px;
	line-height: 1.33333;
	font-weight: 900;
`;

const Description = styled.p`
	margin: 0px;
	font-size: 0.875rem;
	color: #4b5865;
	margin-bottom: 30px;
`;

const ShopGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 20px;
	margin-top: 20px;
`;

const ShopCard = styled.div`
	background-color: white;
	border-radius: 8px;
	padding: 24px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	cursor: pointer;
	transition: transform 0.3s ease, box-shadow 0.3s ease;
	display: flex;
	flex-direction: column;
	gap: 16px;
	position: relative;

	&:hover {
		transform: translateY(-5px);
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	}
`;

const EditButton = styled.button`
	position: absolute;
	top: 12px;
	right: 12px;
	background-color: #f0f4ff;
	border: none;
	border-radius: 50%;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	opacity: 0.7;
	transition: opacity 0.2s ease, background-color 0.2s ease;

	&:hover {
		opacity: 1;
		background-color: #e0e7ff;
	}
`;

const ShopAvatar = styled.div`
	width: 60px;
	height: 60px;
	border-radius: 12px;
	background-color: #e4e8fa;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	font-weight: bold;
	color: #3a5bd8;
`;

const AddShopCard = styled(ShopCard)`
	border: 2px dashed #e0e0e0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background-color: #fafafa;

	&:hover {
		border-color: #0288d1;
		background-color: #f5f5f5;
	}
`;

const UserProfile = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
`;

const InvitationsContainer = styled(Paper)`
	padding: 20px;
	margin-bottom: 30px;
	background-color: #f8f8ff;
	border-left: 4px solid #4a65d5;
`;

const InvitationsList = styled.div`
	margin-top: 15px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const InvitationItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 15px;
	background-color: white;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const InvitationActions = styled.div`
	display: flex;
	gap: 10px;
`;

interface Shop {
	id: string;
	name: string;
	description: string;
	hash_id: string;
	currency: CurrencyCode;
	subdomain?: string;
	logo_url?: string;
	payment_instruction?: string;
	storefront_template?: string;
	date_created: string;
	is_owner?: boolean;
}

interface ShopFormData {
	name: string;
	description: string;
	currency: CurrencyCode;
	subdomain?: string;
	logo?: File | null;
	logo_url?: string;
	payment_instruction?: string;
	storefront_template?: string;
}

interface ShopInvitation {
	invited_by_name: string;
	id: string;
	token: string;
	shop_id: string;
	shop_name: string;
	inviter_name: string;
	status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
	date_created: string;
}

export default function ShopSelector() {
	const [shops, setShops] = useState<Shop[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
	const [invitations, setInvitations] = useState<ShopInvitation[]>([]);
	const [invitationsLoading, setInvitationsLoading] = useState<boolean>(false);
	const [formData, setFormData] = useState<ShopFormData>({
		name: '',
		description: '',
		currency: CurrencyCode.USD,
		subdomain: '',
		logo: null,
		logo_url: '',
		payment_instruction: '',
		storefront_template: 'template1',
	});
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [isCreating, setIsCreating] = useState<boolean>(false);

	const navigate = useNavigate();
	const user = useSelector((state: any) => state.auth.user);

	// Menu handlers
	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		apiClient.auth.logout();
		navigate(routes.LOGIN);
	};

	// Shop form handlers
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			setFormData({
				...formData,
				logo: e.target.files[0],
			});
		}
	};

	const handleCreateShop = () => {
		// Clear form data and set create mode
		setFormData({
			name: '',
			description: '',
			currency: CurrencyCode.USD,
			subdomain: '',
			logo: null,
			logo_url: '',
			payment_instruction: '',
			storefront_template: 'template1',
		});
		setIsEditMode(false);
		setSelectedShop(null);
		setIsPanelVisible(true);
	};

	const handleEditShop = (shop: Shop, e: React.MouseEvent) => {
		// Prevent card click event from firing
		e.stopPropagation();

		// Set form data to the selected shop
		setFormData({
			name: shop.name,
			description: shop.description || '',
			currency: shop.currency,
			subdomain: shop.subdomain || '',
			logo: null,
			logo_url: shop.logo_url || '',
			payment_instruction: shop.payment_instruction || '',
			storefront_template: shop.storefront_template || 'template1',
		});
		setIsEditMode(true);
		setSelectedShop(shop);
		setIsPanelVisible(true);
	};

	const handleClosePanel = () => {
		setIsPanelVisible(false);
		setIsEditMode(false);
		setSelectedShop(null);
	};

	const handleSubmitShop = () => {
		if (!formData.name) {
			notify('error', 'Business name is required');
			return;
		}

		setIsCreating(true);

		// Create a FormData object for file upload
		const shopFormData = new FormData();
		shopFormData.append('name', formData.name);
		shopFormData.append('description', formData.description || '');
		shopFormData.append('currency', formData.currency);
		if (formData.subdomain) shopFormData.append('subdomain', formData.subdomain);
		if (formData.logo) shopFormData.append('logo', formData.logo);
		if (formData.payment_instruction) shopFormData.append('payment_instruction', formData.payment_instruction);
		if (formData.storefront_template) shopFormData.append('storefront_template', formData.storefront_template);

		if (isEditMode && selectedShop) {
			// Update existing shop
			apiClient
				.update(`/shops/${selectedShop.id}`, shopFormData, true)
				.then((response: any) => {
					// Refresh the shops list
					fetchShops();
					setIsPanelVisible(false);
					setIsEditMode(false);
					setSelectedShop(null);
					notify('success', 'Business updated successfully');
				})
				.catch((err) => {
					console.error('Error updating business:', err);
					notify('error', 'Failed to update business');
				})
				.finally(() => {
					setIsCreating(false);
				});
		} else {
			// Create new shop
			apiClient
				.post('/shops', shopFormData)
				.then((response: any) => {
					// Add the new shop to the list
					fetchShops();
					setIsPanelVisible(false);
				})
				.catch((err) => {
					console.error('Error creating business:', err);
				})
				.finally(() => {
					setIsCreating(false);
				});
		}
	};

	const fetchShops = () => {
		setIsLoading(true);
		apiClient
			.get('/shops')
			.then((response: any) => {
				setShops(response.data || []);
			})
			.catch((err) => {
				console.error('Error fetching businesses:', err);
				notify('error', 'Failed to fetch businesses');
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleSelectShop = async (shop: Shop) => {
		try {
			// Use the apiClient shop selectShop method
			await apiClient.shop.selectShop(shop.hash_id, shop.name, shop.currency, shop.is_owner);

			// Check subscription status after shop selection
			const subscriptionResponse = await apiClient.get('/subscriptions/current');
			const subscriptionData = subscriptionResponse.data;

			// If subscription is expired, redirect to subscription page
			if (subscriptionData.subscriptionStatus?.effectiveStatus === 'expired') {
				navigate(routes.SUBSCRIPTION);
				// notify('warning', 'Your subscription has expired. Please renew to continue using the application.');
				return;
			}

			// If in grace period, redirect to subscription page with different message
			if (subscriptionData.subscriptionStatus?.effectiveStatus === 'grace_period') {
				navigate(routes.SUBSCRIPTION);
				notify('info', 'Your subscription is in grace period. Please renew to avoid service interruption.');
				return;
			}

			// Navigate to dashboard if subscription is active
			navigate(routes.DASHBOARD);
		} catch (error) {
			console.error('Error selecting shop:', error);
			notify('error', 'Failed to select shop. Please try again.');
		}
	};

	// Fetch shop invitations from the server
	const fetchInvitations = () => {
		setInvitationsLoading(true);
		apiClient
			.get('/invitations/user')
			.then((response: any) => {
				// Filter to only show pending invitations
				const pendingInvitations =
					response.data?.filter((invitation: ShopInvitation) => invitation.status === 'pending') || [];
				setInvitations(pendingInvitations);
			})
			.catch((err) => {
				console.error('Error fetching invitations:', err);
			})
			.finally(() => {
				setInvitationsLoading(false);
			});
	};

	// Handle accepting an invitation
	const handleAcceptInvitation = (invitation: ShopInvitation) => {
		apiClient
			.update(`/invitations/token/${invitation.token}/accept`, null, true)
			.then(() => {
				// Refresh invitations and shops
				fetchInvitations();
				fetchShops();
				notify('success', `You've joined ${invitation.shop_name}`);
			})
			.catch((err) => {
				console.error('Error accepting invitation:', err);
				notify('error', 'Failed to accept invitation');
			});
	};

	// Handle rejecting an invitation
	const handleRejectInvitation = (invitation: ShopInvitation) => {
		apiClient
			.update(`/invitations/token/${invitation.token}/reject`, null, true)
			.then(() => {
				// Refresh invitations
				fetchInvitations();
				notify('info', `Invitation to ${invitation.shop_name} declined`);
			})
			.catch((err) => {
				console.error('Error rejecting invitation:', err);
				notify('error', 'Failed to decline invitation');
			});
	};

	useEffect(() => {
		console.log('inside mada');
		ReactGA.send({
			hitType: 'pageview',
			page: '/shop-selector',
			title: 'Shop Selector',
		});

		// Fetch shops and invitations
		fetchShops();
		fetchInvitations();
	}, []);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<CircularProgress />
			</div>
		);
	}

	return (
		<Wrapper>
			<Header className="p-2 flex justify-between items-center" style={{ backgroundColor: 'white' }}>
				<div className="text-black" style={{ fontWeight: 'bold', fontSize: '19px' }}>
					Vend<span style={{ color: '#3a5bd9' }}>Ready</span>
				</div>
				{user && (
					<UserProfile onClick={handleMenuOpen}>
						<Avatar
							sx={{
								bgcolor: '#e4e8fa',
								color: '#3a5bd8',
								width: '30px',
								height: '30px',
								fontSize: '13px',
							}}
						>
							{user.firstname.charAt(0).toUpperCase()}
							{user.surname.charAt(0).toUpperCase()}
						</Avatar>
						<div>
							<p className="text-xs font-medium" style={{ lineHeight: '1rem' }}>
								{user.firstname} {user.surname}
							</p>
							<p className="text-xs text-gray-500">{user.email}</p>
						</div>
					</UserProfile>
				)}
				<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
					<MenuItem onClick={handleLogout}>
						<LogoutIcon fontSize="small" sx={{ mr: 1 }} />
						Logout
					</MenuItem>
				</Menu>
			</Header>

			{/* Invitations Section - Only shown if there are pending invitations */}
			{invitationsLoading ? (
				<div className="flex justify-center my-4">
					<CircularProgress size={24} />
				</div>
			) : (
				<>
					{invitations.length > 0 && (
						<InvitationsContainer elevation={0}>
							<div className="flex items-center">
								<MailIcon sx={{ color: '#4a65d5', mr: 2 }} />
								<div>
									<Typography variant="h6" fontWeight="bold">
										Invitations
									</Typography>
									<Typography variant="body2" color="text.secondary">
										You've been invited to join the following businesses
									</Typography>
								</div>
							</div>

							<InvitationsList>
								{invitations.map((invitation) => (
									<InvitationItem key={invitation.id}>
										<div>
											<Typography variant="subtitle1" fontWeight="medium">
												{invitation.shop_name}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												Invited by {invitation.invited_by_name}
											</Typography>
										</div>
										<InvitationActions>
											<Button
												variant="outlined"
												size="small"
												color="error"
												startIcon={<CloseIcon />}
												onClick={() => handleRejectInvitation(invitation)}
											>
												Decline
											</Button>
											<Button
												variant="contained"
												size="small"
												color="primary"
												startIcon={<DoneIcon />}
												onClick={() => handleAcceptInvitation(invitation)}
											>
												Accept
											</Button>
										</InvitationActions>
									</InvitationItem>
								))}
							</InvitationsList>
						</InvitationsContainer>
					)}
				</>
			)}

			<div>
				<Title>My Businesses</Title>
				<Description>Choose one of your businesses to manage or create a new one.</Description>

				<ShopGrid>
					{shops.map((shop) => (
						<ShopCard key={shop.id} onClick={() => handleSelectShop(shop)}>
							<EditButton onClick={(e) => handleEditShop(shop, e)}>
								<EditIcon fontSize="small" sx={{ color: '#4a65d5' }} />
							</EditButton>
							{shop.logo_url ? (
								<div className="w-16 h-16 rounded-md overflow-hidden">
									<img src={shop.logo_url} alt={shop.name} className="w-full h-full object-contain" />
								</div>
							) : (
								<ShopAvatar>{shop.name.charAt(0).toUpperCase()}</ShopAvatar>
							)}
							<div>
								<Typography variant="h6" fontWeight="bold">
									{shop.name}
								</Typography>
								<Typography variant="caption" color="text.secondary" display="block">
									{shop.subdomain ? (
										<a
											href={`${generateSubdomainUrl(shop.subdomain)}`} //TODO : change to https for production
											target="_blank"
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()} // Prevents triggering the card's onClick
											className="hover:underline"
											style={{ color: '#3a5bd8' }}
										>
											{shop.subdomain}.{getWebsiteUrl()}
										</a>
									) : shop.description ? (
										shop.description
									) : (
										'No description available'
									)}
								</Typography>
							</div>
						</ShopCard>
					))}

					<AddShopCard onClick={handleCreateShop}>
						<AddIcon sx={{ fontSize: 40, color: '#9e9e9e', mb: 2 }} />
						<Typography variant="h6" align="center" fontWeight="medium" color="text.secondary">
							Add New Business
						</Typography>
					</AddShopCard>
				</ShopGrid>
			</div>

			<SidePanel isVisible={isPanelVisible} handleHidePanel={handleClosePanel}>
				<div className="p-4">
					<Typography variant="h5" component="h2" fontWeight="bold" mb={4}>
						{isEditMode ? 'Edit Business' : 'Create New Business'}
					</Typography>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Business Name*</label>
						<input
							type="text"
							name="name"
							value={formData.name}
							onChange={handleInputChange}
							className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
							required
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
						<textarea
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
							rows={4}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Storefront Subdomain</label>
						<div className="flex items-center">
							<input
								type="text"
								name="subdomain"
								value={formData.subdomain}
								onChange={handleInputChange}
								className="flex-1 px-4 py-2 rounded-l-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
								placeholder="mystore"
							/>
							<span className="bg-gray-100 px-4 py-2 rounded-r-lg border border-l-0 border-gray-300">
								.{getWebsiteUrl()}
							</span>
						</div>
						<p className="text-sm text-gray-500 mt-1">
							This will create a custom URL for your store. Leave empty if you don't want a storefront.
						</p>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
						<div className="relative">
							<label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
								Select or type a currency
							</label>
							<div className="relative">
								<input
									id="currency"
									list="currencies"
									name="currency"
									value={formData.currency}
									onChange={handleInputChange}
									className="w-full px-4 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
									placeholder="e.g., USD, EUR, GBP"
								/>
								<datalist id="currencies">
									{currencies.map((currency) => (
										<option key={currency.cc} value={currency.cc}>
											{currency.name} - {currency.cc}
										</option>
									))}
								</datalist>
							</div>
						</div>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">Payment Instructions</label>
						<textarea
							name="payment_instruction"
							value={formData.payment_instruction || ''}
							onChange={handleInputChange}
							className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
							rows={6}
							placeholder="Enter payment instructions for invoices. Formatting will be preserved."
						/>
						<p className="text-xs text-gray-500 mt-1">
							These payment instructions will appear on customer invoices.
						</p>
					</div>

					{/* Template Selection - Only show for edit mode and if shop has subdomain */}
					{isEditMode && formData.subdomain && (
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 mb-1">Storefront Template</label>
							<div className="grid grid-cols-2 gap-4">
								<div
									className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
										formData.storefront_template === 'template1'
											? 'border-blue-500 bg-blue-50'
											: 'border-gray-300 hover:border-gray-400'
									}`}
									onClick={() => setFormData({ ...formData, storefront_template: 'template1' })}
								>
									<div className="text-center">
										<div className="w-full h-20 bg-gray-100 rounded mb-2 flex items-center justify-center">
											<span className="text-xs text-gray-500">Template 1</span>
										</div>
										<h4 className="font-medium text-sm">Classic Template</h4>
										<p className="text-xs text-gray-500">Clean and simple design</p>
									</div>
								</div>
								<div
									className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
										formData.storefront_template === 'template2'
											? 'border-blue-500 bg-blue-50'
											: 'border-gray-300 hover:border-gray-400'
									}`}
									onClick={() => setFormData({ ...formData, storefront_template: 'template2' })}
								>
									<div className="text-center">
										<div className="w-full h-20 bg-gradient-to-r from-purple-100 to-blue-100 rounded mb-2 flex items-center justify-center">
											<span className="text-xs text-gray-600">Template 2</span>
										</div>
										<h4 className="font-medium text-sm">Modern Template</h4>
										<p className="text-xs text-gray-500">Vibrant and contemporary</p>
									</div>
								</div>
							</div>
							<p className="text-sm text-gray-500 mt-2">
								Choose how your storefront will look to customers.
							</p>
						</div>
					)}

					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-1">Business Logo</label>
						<div className="flex items-start gap-4">
							{/* Logo Preview */}
							<div className="w-24 h-24 overflow-hidden rounded-lg border border-gray-300 flex items-center justify-center bg-gray-50">
								{formData.logo_url ? (
									<img
										src={formData.logo_url}
										alt="Business Logo"
										className="w-full h-full object-contain"
									/>
								) : formData.logo ? (
									<img
										src={URL.createObjectURL(formData.logo)}
										alt="Business Logo"
										className="w-full h-full object-contain"
									/>
								) : (
									<div className="text-gray-400 text-3xl">
										{formData.name ? formData.name.charAt(0).toUpperCase() : '?'}
									</div>
								)}
							</div>
							<div className="flex-1">
								<input
									type="file"
									id="logo"
									accept="image/jpeg,image/png,image/gif,image/webp"
									onChange={handleFileChange}
									className="hidden"
								/>
								<label
									htmlFor="logo"
									className="inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium rounded-md cursor-pointer transition-colors"
								>
									{formData.logo || formData.logo_url ? 'Change Logo' : 'Upload Logo'}
								</label>
								{(formData.logo || formData.logo_url) && (
									<button
										type="button"
										onClick={() => setFormData({ ...formData, logo: null, logo_url: '' })}
										className="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-800"
									>
										Remove
									</button>
								)}
								<p className="text-xs text-gray-500 mt-1">
									Recommended size: 200×200px. Max size: 2MB.
									<br />
									Allowed formats: JPG, PNG, GIF, WEBP
								</p>
							</div>
						</div>
					</div>

					<Button
						variant="contained"
						color="primary"
						fullWidth
						onClick={handleSubmitShop}
						disabled={isCreating}
						disableElevation
						sx={{ py: 1.5 }}
						startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
					>
						{isCreating
							? isEditMode
								? 'Updating...'
								: 'Creating...'
							: isEditMode
							? 'Update Business'
							: 'Create Business'}
					</Button>
				</div>
			</SidePanel>
		</Wrapper>
	);
}
